import { useEffect, useRef } from "react";

/**
 * Whether anything on screen holds unsaved edits, app-wide. Every guarded dialog (Modal, Sheet, FormDialog via
 * `useCloseGuard`) registers its own "do I have edits" check while mounted; a surface outside a dialog that holds a
 * draft (a chat composer) registers one with {@link useUnsavedEdits}. The live updater reads it to avoid swapping the
 * app's code while a draft is open. A check, not a stored flag, so it can never go stale.
 */
const checks = new Set<() => boolean>();

/** Register a check; returns the unregister. */
export function registerUnsavedEdits(check: () => boolean): () => void {
  checks.add(check);
  return () => {
    checks.delete(check);
  };
}

/** True when any registered surface reports unsaved edits. A check that throws counts as "no edits". */
export function hasUnsavedEdits(): boolean {
  for (const check of checks) {
    try {
      if (check()) return true;
    } catch {
      // a broken check must never hold an update back forever
    }
  }
  return false;
}

/** Register `hasEdits` for as long as the calling component is mounted. Reads the latest value on every check. */
export function useUnsavedEdits(hasEdits: boolean): void {
  const ref = useRef(hasEdits);
  ref.current = hasEdits;
  useEffect(() => registerUnsavedEdits(() => ref.current), []);
}
